import React, { useState } from 'react';
import { Send } from 'lucide-react';
import { FaPhoneAlt, FaEnvelope, FaMapMarkerAlt } from "react-icons/fa";
import { useTranslation } from 'react-i18next';

const Contactform: React.FC = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('loading');

    try {
      const res = await fetch('https://formspree.io/f/xlgppjqj', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (res.ok) {
        setStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <section className="bg-white py-30 md:py-24 lg:py-30 px-4 md:px-6 lg:px-5">
      <div className="max-w-[1190px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-0">

          {/* Main Heading */}
          <div className="lg:col-span-8 xl:col-span-8">
            <h1 className="text-5xl md:text-5xl lg:text-6xl xl:text-[70px] font-medium text-[#002f37] leading-[1.3] tracking-tight">
              {t('contact_heading_line1')} <br /> <span className="font-semibold">{t('contact_heading_line2')}</span>
            </h1>
          </div>

          {/* Contact Information */}
          <div className="lg:col-span-5 xl:col-span-5 pt-2 lg:pt-12 px-2">
            <div className="space-y-1">
              <h3 className="text-[#002f37] text-xl lg:text-2xl xl:text-[26px] font-medium leading-tight mb-4 lg:mb-5 max-w-full lg:max-w-[360px]">
                {t('contact_intro')}
              </h3>
              <div className="space-y-3 lg:space-y-2">
                <a
                  href="https://wa.me/923066665542"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center gap-3 text-[#002f37] text-base lg:text-lg hover:text-[#121212] transition-colors py-1"
                >
                  <FaPhoneAlt className="text-[14px] group-hover:opacity-70 transition-opacity" />
                  <span className="underline">{t('contact_phone_value')}</span>
                </a>
                <a href="mailto:info@saleementerprises.com" className="group flex items-center gap-3 text-[#002f37] text-base lg:text-lg hover:text-[#121212] transition-colors py-1">
                  <FaEnvelope className="text-[14px] group-hover:opacity-80 transition-opacity" />
                  <span className="underline">{t('contact_email_value')}</span>
                </a>
                <div className="flex items-start gap-3 text-[#002f37] text-base lg:text-lg pt-2">
                  <FaMapMarkerAlt className="text-[14px] mt-1 flex-shrink-0" />
                  <p className="leading-tight max-w-[210px]">
                    {t('contact_address_line1')}<br />
                    {t('contact_address_line2')}<br />
                    {t('contact_address_line3')}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-7 xl:col-span-7 pt-8 lg:pt-24 xl:pt-[40px]">

            {status === 'success' ? (
              <div className="flex flex-col justify-center h-full py-10">
                <h3 className="text-2xl font-semibold text-[#002f37] mb-2">{t('contact_success_title')}</h3>
                <p className="text-[#121212]/60 mb-6">{t('contact_success_desc')}</p>
                <button
                  onClick={() => setStatus('idle')}
                  className="text-sm underline text-[#002f37] w-fit"
                >
                  {t('contact_success_reset')}
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-0">

                {/* Name */}
                <div className="relative mb-6 lg:mb-[25px]">
                  <input
                    type="text" name="name" value={formData.name} onChange={handleChange}
                    onFocus={() => setFocusedField('name')} onBlur={() => setFocusedField(null)}
                    placeholder={t('contact_input_name')}
                    className="w-full h-[50px] bg-transparent border-0 border-b border-[#C2C2C2] text-[#121212] placeholder-[#121212] text-base focus:outline-none transition-all duration-300 px-0"
                    required
                  />
                  <div className={`absolute bottom-0 left-0 h-[2px] bg-[#121212] transition-all duration-300 ${focusedField === 'name' ? 'w-full' : 'w-0'}`} />
                </div>

                {/* Email */}
                <div className="relative mb-6 lg:mb-[25px]">
                  <input
                    type="email" name="email" value={formData.email} onChange={handleChange}
                    onFocus={() => setFocusedField('email')} onBlur={() => setFocusedField(null)}
                    placeholder={t('contact_input_email')}
                    className="w-full h-[50px] bg-transparent border-0 border-b border-[#C2C2C2] text-[#121212] placeholder-[#121212] text-base focus:outline-none transition-all duration-300 px-0"
                    required
                  />
                  <div className={`absolute bottom-0 left-0 h-[2px] bg-[#121212] transition-all duration-300 ${focusedField === 'email' ? 'w-full' : 'w-0'}`} />
                </div>

                {/* Subject */}
                <div className="relative mb-6 lg:mb-[25px]">
                  <input
                    type="text" name="subject" value={formData.subject} onChange={handleChange}
                    onFocus={() => setFocusedField('subject')} onBlur={() => setFocusedField(null)}
                    placeholder={t('contact_input_subject')}
                    className="w-full h-[50px] bg-transparent border-0 border-b border-[#C2C2C2] text-[#121212] placeholder-[#121212] text-base focus:outline-none transition-all duration-300 px-0"
                    required
                  />
                  <div className={`absolute bottom-0 left-0 h-[2px] bg-[#121212] transition-all duration-300 ${focusedField === 'subject' ? 'w-full' : 'w-0'}`} />
                </div>

                {/* Message */}
                <div className="relative mb-6 lg:mb-[15px] mt-6">
                  <textarea
                    name="message" value={formData.message} onChange={handleChange}
                    onFocus={() => setFocusedField('message')} onBlur={() => setFocusedField(null)}
                    placeholder={t('contact_input_message')} rows={3}
                    className="w-full min-h-[40px] bg-transparent border-0 border-b border-[#C2C2C2] text-[#121212] placeholder-[#121212] text-base focus:outline-none transition-all duration-300 resize-none px-0 py-2"
                    required
                  />
                  <div className={`absolute bottom-0 left-0 h-[2px] bg-[#121212] transition-all duration-300 ${focusedField === 'message' ? 'w-full' : 'w-0'}`} />
                </div>

                {/* Error message */}
                {status === 'error' && (
                  <p className="text-red-500 text-sm pt-2">{t('contact_error_message')}</p>
                )}

                {/* Submit Button */}
                <div className="pt-6 lg:pt-4">
                  <button
                    type="submit"
                    disabled={status === 'loading'}
                    className="group relative h-[60px] lg:h-[65px] px-8 lg:px-10 bg-[#002f37] text-white text-base border border-[#121212] rounded-lg overflow-hidden hover:bg-transparent transition-all duration-500 flex items-center gap-2 shadow-lg hover:shadow-xl disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <span className="relative z-10 group-hover:text-[#121212] transition-colors duration-500">
                      {status === 'loading' ? t('contact_sending') : t('contact_send')}
                    </span>
                    <Send className="w-3 h-3 relative z-10 group-hover:translate-x-1 group-hover:text-[#121212] transition-all duration-500" />
                    <span className="absolute inset-0 bg-white transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
                  </button>
                </div>

              </form>
            )}
          </div>

        </div>
      </div>
    </section>
  );
};

export default Contactform;